// Externals imports.
import React, { ReactElement, useContext, useEffect, useRef, useState } from 'react'
import { ConfigureReport as ConfigureReportLanguage } from '../../../i18n'
import CLanguageHandler from '../../NonReact/Languages/LanguageHandler'
import { SERVLET_COMMAND, ServletCmds } from '../../NonReact/constants/ServletCommands'
import Modal from '../Common/Components/Modal'
import { SERVLET_ACTION, ServletActions } from '../../NonReact/constants/React/ServletActions/ServletActions'
// Contexts imports.
import {
    LanguageContextProvider,
    MessageContextProvider,
    ServletPathContext,
    UserInfoContextProvider
} from '../Common/Contexts'
import ConfigureReportSettingsContext from '../Common/Contexts/ConfigureReportSettingsContext'
import { ModalContextProvider } from '../Common/Contexts/ModalContext'
import { clearModalHelpTopic } from '../Common/Utils/HelpPortal'
// Sub imports.
import { AdvancedModeContextProvider } from '../Common/Contexts/AdvancedModeContext'
import { ServletPathContextProvider } from '../Common/Contexts/ServletPathContext'
import ConfigurationTabs from './ConfigurationTabs'
import { createCustomWindow } from '../Common/Utils/WindowsFunctions'
import { AkixiWindow } from '../../NonReact/common/Types/Types'
// CSS imports. Commented out because of current build process.
// import './configureReport.scss';

/**
 * Component properties type.
 */
type Props = {
    popoutMode?: boolean
    modalTitle: string
    reportID?: string
    isModalOpen?: boolean
    setIsModalOpen?: (open: boolean) => void
    container?: HTMLDivElement
    servletPath?: string
    refreshReportListDataAndStartReport?: (reportID: string, reportDesc: string) => void
    uuid: string
    defaultFolderName?: string
}

/**
 * Report configuration modal with anchor element.
 *
 * @param popoutMode - Are we in popout mode?
 * @param modalTitle The localised title of the modal.
 * @param reportID The report identifier for modify action. In case of add action it will be undefined.
 * @param isModalOpen
 * @param setIsModalOpen
 * @param container The container that modal will use as a parent DOM element.
 * @param servletPath
 * @param refreshReportListDataAndStartReport Callback method to reload report list data and start report with provided ID - only needed in case of Add action.
 */
const ConfigureReport: React.FC<Props> = ({
    popoutMode,
    modalTitle,
    reportID,
    isModalOpen,
    setIsModalOpen,
    container,
    servletPath,
    refreshReportListDataAndStartReport,
    uuid,
    defaultFolderName
}: Props): ReactElement => {
    const language: CLanguageHandler = CLanguageHandler.getInstance()
    language.loadBundle('ConfigureReport')

    const contextServletPath: Nullable<string> = useContext(ServletPathContext)

    const { isDateTimeOnly } = useContext(ConfigureReportSettingsContext)

    const [isModalOpenInternal, setIsModalOpenInternal] = useState<boolean>(
        setIsModalOpen ? (isModalOpen as boolean) : true
        // Initial state is defined externally in case we have propagated the setter and value of "open" state from
        // parent. Otherwise the modal is instantiated from JSP or old JS logic which means that it is open by default.
    )

    const configTabsRef: React.MutableRefObject<unknown> = useRef({})

    useEffect(() => {
        // If the modal window is controlled by the parent react component,
        // then we have to update the internal state accordingly.
        if (setIsModalOpen) {
            setIsModalOpenInternal(isModalOpen as boolean)
        }
    }, [isModalOpen])

    const closeModal = (): void => {
        if (setIsModalOpen) {
            // Component is controlled externally.
            setIsModalOpen(false)
        } else {
            // Component is controlled internally and it is initialised by the JSP / old JS logic.
            setIsModalOpenInternal(false)
        }
    }

    const withServletPathContextIfDefined = (child: ReactElement): ReactElement =>
        servletPath ? <ServletPathContextProvider value={servletPath}>{child}</ServletPathContextProvider> : child

    /**
     * Modify report onclick method.
     */
    const modifyReport = async (): Promise<void> => {
        const params: URLSearchParams = new URLSearchParams()

        params.set(SERVLET_COMMAND, ServletCmds.RPT_CHANGE)

        if (reportID) {
            params.set(SERVLET_ACTION, ServletActions.SHOW_CHG)
            params.set('ID', reportID)
        } else {
            params.set(SERVLET_ACTION, ServletActions.SHOW_ADD)
        }

        params.set('ShowConfigReport', 'true')

        const popoutWindow: Nullable<Window> = await createCustomWindow(
            `${servletPath ?? contextServletPath}?${params.toString()}`,
            '_blank',
            'menubar=0,location=0,resizable=1,status=0,scrollbars=0,directories=0,dependent=1,innerWidth=1060px,innerHeight=836px,left=3px,top=727px'
        )

        if (popoutWindow) {
            const $popoutWindow: AkixiWindow = popoutWindow as AkixiWindow
            $popoutWindow.configurationTabs = configTabsRef.current
            $popoutWindow.popoutUUID = uuid
        }
    }

    return (
        <ModalContextProvider
            modalInput={{
                closeModal,
                popoutMode: !!popoutMode,
                popoutFunc: isDateTimeOnly
                    ? undefined
                    : (): void => {
                          modifyReport()
                          closeModal()
                      }
            }}
        >
            <Modal
                classNames={`configure-report-modal ${isDateTimeOnly ? 'minimal' : ''}`}
                isOpen={isModalOpenInternal}
                onClose={closeModal}
                closeOnBackdropClick={true}
                isClosableWithXButton={true}
                inlineExitPromptSettings={{
                    message: language.get(ConfigureReportLanguage.ConfigureReport_PromptMessage),
                    confirmButtonText: language.get(ConfigureReportLanguage.ConfigureReport_PromptConfirm),
                    cancelButtonText: language.get(ConfigureReportLanguage.ConfigureReport_PromptCancel)
                }}
                onExited={(): void => {
                    if (!setIsModalOpen) {
                        // Component is controlled internally and it is initialised by the JSP / old JS logic.
                        //
                        // Container is always created when the modal is being open from JSP / old JS function
                        // so that it has to be always removed.
                        container?.parentNode?.removeChild(container)
                    }
                    // Remove the contextual doc topic ID from the window that is used
                    // in old JSP logic that handles F1 key press.
                    // When removed the previous logic will work and the modal window doc topic will be not considered.
                    clearModalHelpTopic()
                }}
            >
                {withServletPathContextIfDefined(
                    <MessageContextProvider>
                        <UserInfoContextProvider>
                            <LanguageContextProvider>
                                <AdvancedModeContextProvider>
                                    <div className='modal-title-report-config'>{modalTitle}</div>
                                    <ConfigurationTabs
                                        uuid={uuid}
                                        stateRef={configTabsRef}
                                        reportID={reportID}
                                        closeModal={closeModal}
                                        refreshReportListDataAndStartReport={refreshReportListDataAndStartReport}
                                        defaultFolderName={defaultFolderName}
                                    />
                                </AdvancedModeContextProvider>
                            </LanguageContextProvider>
                        </UserInfoContextProvider>
                    </MessageContextProvider>
                )}
            </Modal>
        </ModalContextProvider>
    )
}

export default ConfigureReport
